/* START_MODULE_CONTRACT
 * PURPOSE: Визуальный блок этапа конвейера — одна роль или группа совместных ролей.
 * SCOPE: ProjectRoleStageGroup.
 * DEPENDS: M-FE-ROLES, projectRoleLayoutUtils
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectRoleStageGroup - карточка этапа в лесенчатой раскладке ролей.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Этап конвейера: одиночная роль или зона совместной работы.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ProjectRoleItem from './ProjectRoleItem';
import { createInteractiveSurfaceTransition } from '../../../theme/appTheme';

const STAIR_STEP_SPACING = 3;

/* START_METHOD_ProjectRoleStageGroup */
/* START_CONTRACT: ProjectRoleStageGroup
 * PURPOSE: Отобразить этап конвейера с отступом «лесенкой» и визуальным выделением совместных ролей.
 * INPUTS: { stage, stageIndex, totalStages, allRoles, onEdit, onDelete }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: ProjectRoleStageGroup
 */
export default function ProjectRoleStageGroup({
  stage,
  stageIndex,
  totalStages,
  allRoles,
  onEdit,
  onDelete,
}) {
  const theme = useTheme();
  const { roles, isConcurrent } = stage;
  const isLast = stageIndex === totalStages - 1;
  const stageLabel = String(stageIndex + 1).padStart(2, '0');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        pl: stageIndex * STAIR_STEP_SPACING,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: 32,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.25,
        }}
        aria-hidden
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: isConcurrent
              ? alpha(theme.palette.primary.main, 0.14)
              : alpha(theme.palette.text.primary, 0.06),
            color: isConcurrent ? 'primary.main' : 'text.secondary',
            boxShadow: `inset 0 0 0 1px ${alpha(
              isConcurrent ? theme.palette.primary.main : theme.palette.divider,
              isConcurrent ? 0.35 : 0.8,
            )}`,
          }}
        >
          {stageLabel}
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 20,
              mt: 0.75,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1.5 }}>
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            bgcolor: isConcurrent
              ? alpha(theme.palette.primary.main, 0.03)
              : 'background.paper',
            borderColor: isConcurrent
              ? alpha(theme.palette.primary.main, 0.28)
              : 'divider',
            boxShadow: isConcurrent
              ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.06)}`
              : 'none',
            transition: createInteractiveSurfaceTransition(theme, ['border-color', 'box-shadow']),
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }}
        >
          {isConcurrent && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: alpha(theme.palette.primary.main, 0.12),
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <CallSplitIcon sx={{ fontSize: 16, color: 'primary.main', transform: 'rotate(90deg)' }} />
              <Typography variant="caption" fontWeight={600} color="primary.main">
                Совместная работа
              </Typography>
              <Chip
                label={`${roles.length} роли`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            </Box>
          )}

          {roles.map((role, roleIndex) => (
            <ProjectRoleItem
              key={role.id}
              role={role}
              allRoles={allRoles}
              onEdit={onEdit}
              onDelete={onDelete}
              showConcurrentLabels={!isConcurrent}
              isLast={roleIndex === roles.length - 1}
            />
          ))}
        </Paper>
      </Box>
    </Box>
  );
}
/* END_METHOD_ProjectRoleStageGroup */
